// Hợp đồng dữ liệu phân hệ GALA — CHÉP từ backend (nguồn sự thật là code):
//   hp-media-server-staging/src/gala/dtos/event.dto.ts           (CreateGalaEventDto, BlockDto, CategoryDto, enum)
//   hp-media-server-staging/src/gala/entities/GalaEvent.ts       (cột sự kiện, trạng thái)
//   hp-media-server-staging/src/gala/entities/GalaBlock.ts       (loại khối, quyền xem)
//   hp-media-server-staging/src/gala/entities/GalaCategory.ts    (hạng mục, ô chỉ số)
//   hp-media-server-staging/src/gala/gala-event.service.ts       (GalaAdminEvent: thêm coverImageUrl / heroImageUrl)
//   docs/gala/01-dac-ta-2026-10-13.md mục 3.2 (dạng `data` của từng loại khối)
// Sửa bên đó thì sửa cả ở đây.
//
// Thời gian: server lưu/trả ISO UTC; admin nhập và xem theo GIỜ VIỆT NAM (+07:00 cố định,
// Việt Nam không có giờ mùa hè) nên đổi tay bằng toVnInput / fromVnInput, không cần thư viện múi giờ.

export type GalaEventStatus = 'DRAFT' | 'PUBLISHED' | 'ENDED';
export type GalaVenueVisibility = 'ALL' | 'TICKET';
export type GalaBlockKind =
  | 'TEXT'
  | 'ROADMAP'
  | 'GALLERY'
  | 'TALLY'
  | 'NOTICE'
  | 'SCHEDULE'
  | 'LINK';
export type GalaBlockVisibility = 'ALL' | 'TICKET' | 'GROUPS';

/** Sự kiện cho trang quản trị (các cột + URL ảnh). Ngày là chuỗi ISO UTC qua JSON. */
export interface GalaEvent {
  id: number;
  title: string;
  subtitle: string | null;
  eyebrow: string | null;
  eventAt: string | null;
  doorsOpenAt: string | null;
  tbaText: string | null;
  venueName: string | null;
  venueAddress: string | null;
  mapUrl: string | null;
  venueVisibility: GalaVenueVisibility;
  coverImage: string | null;
  heroImage: string | null;
  inviteTotal: number | null;
  status: GalaEventStatus;
  showOnHome: boolean;
  homeCta: string | null;
  qrOnCustom: boolean;
  /** Hạng mục dùng cho bục Top 3 (null = hạng mục mã 01); server từ đợt 3a. */
  podiumCategoryId?: number | null;
  /** Đã công bố sơ đồ chỗ ngồi cho người có vé (SPEC đợt 2a). */
  seatMapVisible: boolean;
  publishedAt: string | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  coverImageUrl: string | null;
  heroImageUrl: string | null;
}

/** Thân PUT /gala/admin/events/:id (mọi trường tuỳ chọn; ngày là ISO UTC hoặc null). */
export interface EventInput {
  title?: string;
  subtitle?: string | null;
  eyebrow?: string | null;
  eventAt?: string | null;
  doorsOpenAt?: string | null;
  tbaText?: string | null;
  venueName?: string | null;
  venueAddress?: string | null;
  mapUrl?: string | null;
  venueVisibility?: GalaVenueVisibility;
  coverImage?: string | null;
  heroImage?: string | null;
  inviteTotal?: number | null;
  homeCta?: string | null;
  qrOnCustom?: boolean;
  podiumCategoryId?: number | null;
}

export interface GalaBlock {
  id: number;
  eventId: number;
  kind: GalaBlockKind;
  title: string | null;
  body: string | null;
  data: Record<string, any> | null;
  visibility: GalaBlockVisibility;
  groups: string[] | null;
  sortOrder: number;
  isHidden: boolean;
}

export interface BlockInput {
  kind: GalaBlockKind;
  title?: string | null;
  body?: string | null;
  data?: Record<string, any> | null;
  visibility?: GalaBlockVisibility;
  groups?: string[] | null;
  isHidden?: boolean;
}

export interface CategoryStat {
  icon: string;
  value: string;
  label: string;
}

export interface GalaCategory {
  id: number;
  eventId: number;
  code: string;
  nameEn: string;
  nameVi: string;
  groupCode: string;
  groupLabel: string;
  medal: string;
  stats: CategoryStat[] | null;
  conditions: string[] | null;
  quantityCount: number | null;
  quantityLabel: string;
  sortOrder: number;
  isHidden: boolean;
}

export interface CategoryInput {
  code: string;
  nameEn: string;
  nameVi: string;
  groupCode: string;
  groupLabel: string;
  medal: string;
  stats?: CategoryStat[];
  conditions?: string[];
  quantityCount?: number | null;
  quantityLabel?: string;
  isHidden?: boolean;
}

/** GET /gala/admin/events/:id */
export interface GalaEventDetail {
  event: GalaEvent;
  categories: GalaCategory[];
  blocks: GalaBlock[];
}

export interface SeedResult {
  categoriesAdded: number;
  blocksAdded: number;
}

/** Số liệu vé của một sự kiện, hiện ở danh sách (lấy từ API vé + check-in). */
export interface EventCounts {
  issued: number;
  checkedIn: number;
}

// ---------- Nhãn hiển thị ----------

export const STATUS_META: Record<GalaEventStatus, { text: string; color: string }> = {
  DRAFT: { text: 'Bản nháp', color: 'default' },
  PUBLISHED: { text: 'Đã công bố', color: 'green' },
  ENDED: { text: 'Đã kết thúc', color: 'blue' },
};

export const BLOCK_KIND_META: Record<GalaBlockKind, { text: string; hint: string }> = {
  TEXT: { text: 'Văn bản', hint: 'Đoạn giới thiệu; **chữ đậm** được phép.' },
  ROADMAP: { text: 'Lộ trình', hint: 'Các chặng nối tiếp, mỗi chặng một màu.' },
  GALLERY: { text: 'Thư viện ảnh', hint: 'Lưới ảnh, bấm vào phóng to.' },
  TALLY: { text: 'Tổng kết', hint: 'Ô số liệu lớn kèm bảng dòng.' },
  NOTICE: { text: 'Lưu ý', hint: 'Hộp lưu ý có biểu tượng.' },
  SCHEDULE: { text: 'Chương trình', hint: 'Các mốc giờ của đêm Gala.' },
  LINK: { text: 'Liên kết', hint: 'Một nút mở đường dẫn ngoài.' },
};

export const BLOCK_KINDS = Object.keys(BLOCK_KIND_META) as GalaBlockKind[];

export const VISIBILITY_OPTIONS: { value: GalaBlockVisibility; label: string }[] = [
  { value: 'ALL', label: 'Tất cả mọi người' },
  { value: 'TICKET', label: 'Chỉ người có vé' },
  { value: 'GROUPS', label: 'Theo nhóm khách' },
];

/** Nhóm hạng mục dùng làm bộ lọc trong app (tự sinh từ groupCode có mặt). */
export const GROUP_PRESETS: { code: string; label: string }[] = [
  { code: 'CANHAN', label: 'Creator cá nhân' },
  { code: 'NHOM', label: 'Nhóm LIVE' },
  { code: 'AGENCY', label: 'Agency' },
  { code: 'NPC', label: 'NPC' },
];

export const MEDAL_KEYS = ['pink', 'rose', 'gold', 'cyan', 'red'] as const;
export type MedalKey = typeof MEDAL_KEYS[number];

export const MEDAL_LABEL: Record<MedalKey, string> = {
  pink: 'Hồng',
  rose: 'Hồng đào',
  gold: 'Vàng',
  cyan: 'Xanh ngọc',
  red: 'Đỏ',
};

export const isMedalKey = (v: string): v is MedalKey => (MEDAL_KEYS as readonly string[]).includes(v);

// ---------- Giờ Việt Nam <-> ISO UTC ----------

const VN_OFFSET_MS = 7 * 3600e3;
const pad = (n: number) => String(n).padStart(2, '0');

/** ISO UTC -> 'YYYY-MM-DD HH:mm' giờ Việt Nam; rỗng khi không có hoặc sai. */
export function toVnInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms + VN_OFFSET_MS);
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
    ` ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  );
}

const VN_INPUT = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/;

/** 'YYYY-MM-DD HH:mm' giờ Việt Nam -> ISO UTC (có mili-giây, đuôi Z); null khi rỗng hoặc sai. */
export function fromVnInput(text: string | null | undefined): string | null {
  const m = VN_INPUT.exec((text ?? '').trim());
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number);
  const utc = Date.UTC(y, mo - 1, d, h, mi);
  const back = new Date(utc);
  // Date.UTC tự "tràn" ngày/giờ sai (tháng 13...) -> so ngược để từ chối
  if (
    back.getUTCFullYear() !== y ||
    back.getUTCMonth() !== mo - 1 ||
    back.getUTCDate() !== d ||
    back.getUTCHours() !== h ||
    back.getUTCMinutes() !== mi
  ) {
    return null;
  }
  return new Date(utc - VN_OFFSET_MS).toISOString();
}

/** 'DD/MM/YYYY HH:mm' giờ Việt Nam để hiện trong bảng; '—' khi chưa chốt. */
export function formatVn(iso: string | null | undefined): string {
  const s = toVnInput(iso);
  if (!s) return '—';
  const [date, time] = s.split(' ');
  const [y, mo, d] = date.split('-');
  return `${d}/${mo}/${y} ${time}`;
}

// ---------- Tiện ích ----------

/** Dời `id` lên (-1) / xuống (+1) một bậc; trả mảng mới, không sửa mảng gốc. */
export function moveId(ids: number[], id: number, delta: -1 | 1): number[] {
  const next = [...ids];
  const from = next.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= next.length) return next;
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/** `data` rỗng đúng dạng của từng loại khối (SPEC mục 3.2). */
export function emptyBlockData(kind: GalaBlockKind): Record<string, any> | null {
  switch (kind) {
    case 'ROADMAP':
      return { phases: [] };
    case 'GALLERY':
      return { images: [] };
    case 'TALLY':
      return { boxes: [], rows: [], note: '', pill: '' };
    case 'NOTICE':
      return { icon: '🔔' };
    case 'SCHEDULE':
      return { items: [] };
    case 'LINK':
      return { url: '', label: '' };
    default:
      return null;
  }
}

/** Một dòng tóm tắt nội dung khối để hiện trong danh sách (không phải mở form). */
export function blockSummary(block: Pick<GalaBlock, 'kind' | 'body' | 'data'>): string {
  const d = (block.data ?? {}) as Record<string, any>;
  const len = (v: unknown) => (Array.isArray(v) ? v.length : 0);
  switch (block.kind) {
    case 'ROADMAP':
      return `${len(d.phases)} chặng`;
    case 'GALLERY':
      return `${len(d.images)} ảnh`;
    case 'TALLY':
      return `${len(d.boxes)} ô số liệu, ${len(d.rows)} dòng`;
    case 'SCHEDULE':
      return `${len(d.items)} mốc giờ`;
    case 'LINK':
      return [d.label, d.url].filter(Boolean).join(' · ');
    default:
      return (block.body ?? '').trim();
  }
}

export interface RoadmapPhase {
  step: string;
  title: string;
  period: string;
  body: string;
  color: string;
}
export interface GalleryImage {
  key: string;
  url?: string;
  caption: string;
  ratio: 'tall' | 'wide' | 'square';
}
export interface TallyBox {
  value: string;
  label: string;
}
export interface TallyRow {
  label: string;
  value: string;
  minus: boolean;
}
export interface ScheduleItem {
  time: string;
  title: string;
  note: string;
}

/**
 * Lấy thông báo lỗi tiếng Việt của server (lỗi nghiệp vụ GALA_* là `{ code, message }`).
 * Lỗi kiểm tra dữ liệu của Nest có `message` là MẢNG tiếng Anh -> dùng câu dự phòng.
 */
export function errorText(e: any, fallback: string): string {
  // Lỗi GALA_* có thể kèm `detail` cụ thể hơn câu chung (vd. GALA_INVALID_INPUT "Vé đã bị thu hồi").
  const d = e?.detail;
  if (typeof d === 'string' && d) return d;
  const m = e?.message;
  return typeof m === 'string' && m ? m : fallback;
}

// ---------- Vé mời (Task 14) ----------
// CHÉP từ: hp-media-server-staging/src/gala/entities/GalaTicketTemplate.ts (khung tên / QR / mã tay, tính theo %),
//          entities/GalaTicket.ts, dtos/ticket.dto.ts, gala-ticket.service.ts (TicketCounts, listAdmin),
//          render/ticket-render.service.ts (progress), gala-ticket.service.ts (upsertGuests -> UpsertResult).

export type GalaTicketStatus = 'PENDING_MATCH' | 'ISSUED' | 'REVOKED';
export type TextAlign = 'left' | 'center' | 'right';

export const TICKET_STATUS_META: Record<GalaTicketStatus, { text: string; color: string }> = {
  PENDING_MATCH: { text: 'Chờ gắn tài khoản', color: 'orange' },
  ISSUED: { text: 'Đã phát', color: 'green' },
  REVOKED: { text: 'Đã thu hồi', color: 'red' },
};

/** Khung tên: x/y/w/h theo % ảnh, fontSize theo % chiều rộng ảnh, stroke.width tính px (tối đa 20). */
export interface NameBoxCfg {
  x: number;
  y: number;
  w: number;
  h: number;
  fontSize: number;
  color: string;
  align: TextAlign;
  weight: number;
  uppercase: boolean;
  maxLines: number;
  fontFamily?: string;
  stroke?: { color: string; width: number } | null;
}

/** Khung QR vuông: size theo % chiều rộng ảnh. */
export interface QrBoxCfg {
  x: number;
  y: number;
  size: number;
  bg: string;
  fg: string;
  margin: number;
}

export interface ShortCodeBoxCfg {
  x: number;
  y: number;
  w: number;
  h: number;
  fontSize: number;
  color: string;
  align: TextAlign;
  weight?: number;
}

export interface GalaTemplate {
  id: number;
  eventId: number;
  name: string;
  imageKey: string;
  width: number;
  height: number;
  nameBox: NameBoxCfg;
  qrBox: QrBoxCfg | null;
  shortCodeBox: ShortCodeBoxCfg | null;
  guestGroup: string | null;
  isDefault: boolean;
}

/** Thân POST/PUT .../templates. */
export interface TemplateInput {
  name: string;
  imageKey: string;
  width: number;
  height: number;
  nameBox: NameBoxCfg;
  qrBox: QrBoxCfg | null;
  shortCodeBox?: ShortCodeBoxCfg | null;
  guestGroup: string | null;
  isDefault: boolean;
}

export interface TicketOwner {
  id: number;
  username: string;
  nickName: string;
  avatar: string | null;
}

/** Một dòng GET .../tickets (cột của vé + `user` đã gắn). Ngày là chuỗi ISO UTC. */
export interface GalaTicketRow {
  id: number;
  eventId: number;
  code: string;
  shortCode: string;
  tiktokUid: string | null;
  userId: number | null;
  displayName: string;
  guestGroup: string | null;
  note: string | null;
  templateId: number | null;
  customImageKey: string | null;
  renderedImageKey: string | null;
  renderedWebpKey: string | null;
  renderedAt: string | null;
  renderError: string | null;
  status: GalaTicketStatus;
  revokeReason: string | null;
  source: string;
  user: TicketOwner | null;
  /** Lúc check-in (ISO UTC) nếu vé có lượt vào còn hiệu lực, không thì null (server gắn khi liệt kê vé). */
  checkedInAt: string | null;
  /** Ghế của vé (bàn + số) hoặc null khi chưa xếp. */
  seat: SeatBrief | null;
}

export interface TicketCounts {
  issued: number;
  pending: number;
  revoked: number;
  checkedIn: number;
}

export interface TicketList {
  data: GalaTicketRow[];
  total: number;
  counts: TicketCounts;
}

export interface TicketQuery {
  status?: GalaTicketStatus;
  group?: string;
  q?: string;
  /** 1 = chỉ vé đã có ghế, 0 = chỉ vé chưa có ghế. */
  seated?: 0 | 1;
  page: number;
  limit: number;
}

export interface UpsertError {
  line: number;
  reason: string;
}

/** Kết quả thêm khách (dán / Excel / ảnh / tài khoản). */
export interface UpsertResult {
  created: number;
  updated: number;
  matched: number;
  pending: number;
  errors: UpsertError[];
}

/** GET .../tickets/render-progress; queued > 0 = còn vé đang chờ vẽ. */
export interface RenderProgress {
  total: number;
  rendered: number;
  failed: number;
  queued: number;
}

export interface TicketUpdateInput {
  displayName?: string;
  guestGroup?: string | null;
  note?: string | null;
  templateId?: number | null;
}

/** Mặc định khi tạo mẫu mới: chữ trắng căn giữa ngang ảnh. */
export const DEFAULT_NAME_BOX: NameBoxCfg = {
  x: 10,
  y: 45,
  w: 80,
  h: 10,
  fontSize: 5,
  color: '#ffffff',
  align: 'center',
  weight: 700,
  uppercase: false,
  maxLines: 2,
  fontFamily: 'BeVietnamPro',
  stroke: null,
};

export const DEFAULT_QR_BOX: QrBoxCfg = {
  x: 70,
  y: 70,
  size: 20,
  bg: '#ffffff',
  fg: '#000000',
  margin: 1,
};

/**
 * Nhóm khách gợi ý sẵn ở mọi ô chọn nhóm (mẫu vé, sửa vé, lọc vé, nhắc lịch, khối nội dung).
 * Viết in hoa vì server lưu nhóm in hoa (cleanGroup / normGroup); vẫn gõ được nhóm khác.
 */
export const GUEST_GROUP_PRESETS: readonly string[] = [
  'CREATOR',
  'NPC',
  'GROUP DANCE',
  'AGENCY',
  'KHÁCH MỜI',
  'NGHỆ SĨ',
  'NHÂN SỰ',
];

/** Gợi ý nhóm khách: nhóm có sẵn trước (đúng thứ tự), rồi các nhóm đang dùng khác (A→Z), in hoa, không trùng. */
export function guestGroupOptions(
  ...used: ReadonlyArray<ReadonlyArray<string | null | undefined>>
): string[] {
  const extra = new Set<string>();
  used.forEach((list) =>
    list.forEach((g) => {
      const v = (g ?? '').trim().toUpperCase();
      if (v && !GUEST_GROUP_PRESETS.includes(v)) extra.add(v);
    })
  );
  return [...GUEST_GROUP_PRESETS, ...Array.from(extra).sort((a, b) => a.localeCompare(b, 'vi'))];
}

/** Bỏ dấu + in hoa để so khớp ("khach moi" khớp "KHÁCH MỜI"). */
const foldVi = (s: string) =>
  s.normalize('NFD').replace(/\p{M}/gu, '').replace(/đ/gi, 'D').toUpperCase().trim();

/** Lọc gợi ý ở ô nhóm khách: chứa chữ đang gõ, không phân biệt hoa thường / có dấu. */
export const groupMatches = (input: string, option: string): boolean =>
  foldVi(option).includes(foldVi(input));

/**
 * Lọc gợi ý cho ô nhóm khách dạng AutoComplete — ô vừa là giá trị vừa là chữ tìm: chữ trong ô
 * trùng hẳn một nhóm (vd. mở form vé đang là "VIP") → hiện đủ danh sách để đổi nhóm; đang gõ dở
 * → chỉ nhóm khớp (groupMatches).
 */
export const groupFilter =
  (options: readonly string[]) =>
  (input: string, option?: { value?: unknown }): boolean => {
    const q = foldVi(input);
    if (!q || options.some((o) => foldVi(o) === q)) return true;
    return groupMatches(input, String(option?.value ?? ''));
  };

/** Kiểu chữ mặc định của mã chữ dưới QR: trắng (ảnh vé thường nền tối), căn giữa, đậm. */
export const DEFAULT_CODE_STYLE: { color: string; align: TextAlign; weight: number } = {
  color: '#ffffff',
  align: 'center',
  weight: 700,
};

/** Mã mẫu hiện trong khung mã ở trình soạn (mã thật dài 6 ký tự). */
export const SAMPLE_SHORT_CODE = 'AB12CD';

/** Tên mẫu khi Xem thử: dài, nhiều dấu để thử co chữ. */
export const PREVIEW_NAME = 'Nguyễn Thị Phương Thảo';

// ---------- Check-in / Nhắc lịch (Task 15) ----------
// CHÉP từ: hp-media-server-staging/src/gala/gala-checkin.service.ts (AdminCheckinRow, CheckinStats, StaffBrief),
//          entities/GalaReminder.ts, dtos/reminder.dto.ts.

export interface CheckinRow {
  id: number;
  ticketId: number;
  displayName: string;
  username: string | null;
  guestGroup: string | null;
  note: string | null;
  method: string;
  client: string | null;
  /** ISO UTC giờ vào. */
  at: string;
  byName: string;
  undoneAt: string | null;
  undoneByName: string | null;
  undoReason: string | null;
  seat: SeatBrief | null;
}

export interface CheckinStats {
  issued: number;
  checkedIn: number;
  pending: number;
}

export interface CheckinList {
  data: CheckinRow[];
  total: number;
  stats: CheckinStats;
}

export interface CheckinQuery {
  group?: string;
  page?: number;
  limit?: number;
}

export interface ManualCheckinResult {
  ok: true;
  checkin: { id: number; at: string; byName: string };
  ticket: { id: number; displayName: string; guestGroup: string | null };
}

export interface StaffBrief {
  id: number;
  username: string;
  nickName: string;
  avatar: string | null;
}

export type ReminderKind = 'OFFSET' | 'CUSTOM' | 'NOW';
export type ReminderStatus = 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELLED' | 'SKIPPED';
export type ReminderAudience = 'TICKET' | 'GROUPS';

export interface GalaReminder {
  id: number;
  eventId: number;
  kind: ReminderKind;
  offsetMinutes: number | null;
  sendTimeLocal: string | null;
  /** ISO UTC giờ gửi. */
  sendAt: string;
  title: string;
  body: string;
  audience: ReminderAudience;
  groups: string[] | null;
  status: ReminderStatus;
  sentAt: string | null;
  sentCount: number;
  failCount: number;
}

/** Thêm mốc: OFFSET cần offsetMinutes + sendTimeLocal; CUSTOM cần sendAt (ISO). */
export interface ReminderInput {
  kind?: 'OFFSET' | 'CUSTOM';
  offsetMinutes?: number;
  sendTimeLocal?: string;
  sendAt?: string;
  title?: string;
  body?: string;
  audience?: ReminderAudience;
  groups?: string[];
  /** Chỉ khi sửa: tắt (CANCELLED) / bật lại (SCHEDULED) mà không xoá. */
  status?: 'SCHEDULED' | 'CANCELLED';
}

export interface SendNowInput {
  title: string;
  body: string;
  audience: ReminderAudience;
  groups?: string[];
}

export const REMINDER_STATUS_META: Record<ReminderStatus, { text: string; color: string }> = {
  SCHEDULED: { text: 'Đã hẹn giờ', color: 'processing' },
  SENDING: { text: 'Đang gửi', color: 'warning' },
  SENT: { text: 'Đã gửi', color: 'success' },
  CANCELLED: { text: 'Đã huỷ', color: 'default' },
  SKIPPED: { text: 'Bỏ qua', color: 'default' },
};

// ---------- Đề cử & giải (GALA đợt 3a) ----------
// CHÉP từ: docs/gala/09-dac-ta-de-cu-giai-thuong-2026-10-13.md mục 3–4.2
//          (hp-media-server-staging/src/gala/gala-nominee.service.ts, dtos/nominee.dto.ts). Sửa bên đó thì sửa ở đây.

export type NomineeResult = 'PENDING' | 'WINNER' | 'NOT_WINNER';

export const NOMINEE_RESULT_META: Record<NomineeResult, { text: string; color: string }> = {
  PENDING: { text: 'Chưa có kết quả', color: 'default' },
  WINNER: { text: 'Người thắng', color: 'gold' },
  NOT_WINNER: { text: 'Không thắng', color: 'default' },
};

/** Vé thành viên của một đơn vị được đề cử (tên, @username, trạng thái vé, đã gắn tài khoản chưa). */
export interface NomineeMember {
  ticketId: number;
  displayName: string;
  username: string | null;
  status: GalaTicketStatus;
  hasAccount: boolean;
}

export interface GalaNominee {
  id: number;
  eventId: number;
  categoryId: number;
  displayName: string;
  imageKey: string | null;
  sortOrder: number;
  result: NomineeResult;
  rank: number | null;
  prizeLabel: string | null;
  note: string | null;
  members: NomineeMember[];
}

/** Một hạng mục kèm các đơn vị được đề cử và thời điểm công bố (ISO UTC hoặc null). */
export interface NomineeCategory {
  id: number;
  code: string;
  nameVi: string;
  /** Hạng mục đang ẩn: không công bố được (server trả 400 "Hạng mục đang ẩn"). Thiếu = không ẩn. */
  isHidden?: boolean;
  nomineesAnnouncedAt: string | null;
  winnersAnnouncedAt: string | null;
  nominees: GalaNominee[];
}

/** GET /gala/admin/events/:id/nominees */
export interface NomineeOverview {
  categories: NomineeCategory[];
}

/** POST .../categories/:cid/nominees */
export interface NomineeCreateInput {
  displayName: string;
  ticketIds: number[];
  imageKey?: string | null;
  note?: string | null;
}

/** PUT .../nominees/:nid (mọi trường tuỳ chọn). */
export interface NomineeUpdateInput {
  displayName?: string;
  imageKey?: string | null;
  note?: string | null;
  result?: NomineeResult;
  rank?: number | null;
  prizeLabel?: string | null;
  sortOrder?: number;
}

export interface NomineeLineIssue {
  line: number;
  reason: string;
}

/** POST .../categories/:cid/nominees/paste */
export interface NomineePasteResult {
  created: number;
  updated: number;
  errors: NomineeLineIssue[];
  warnings: NomineeLineIssue[];
}

export type AnnounceStage = 'NOMINEES' | 'WINNERS';

/** POST .../announce-nominees | announce-winners: số người đã được push. */
export interface AnnounceResult {
  pushed: number;
}

/** POST .../nominees/announce-all */
export interface AnnounceAllResult {
  done: number[];
  skipped: { categoryId: number; reason: string }[];
  /** Số người đã được push (gộp theo người: một người nhiều hạng mục = 1 push). */
  pushed?: number;
}

// ---------- Sơ đồ chỗ ngồi (GALA đợt 2a) ----------
// CHÉP từ: hp-media-server-staging/src/gala/gala-seating.service.ts (AdminSeatingView, AdminTableView,
//          AdminSeatView, AdminSeatTicket, SeatingCounts, PasteSeatResult, TableInput, BulkTablesInput, SeatAssignInput),
//          gala.types.ts (SeatBrief), entities/GalaTable.ts, dtos/seating.dto.ts, gala.constants.ts.
// Ngày là chuỗi ISO UTC qua JSON. Sửa bên đó thì sửa cả ở đây.

export type GalaTableShape = 'ROUND' | 'LONG';

/** Ghế của một vé: dùng cho cột "Ghế" và hộp chọn người. */
export interface SeatBrief {
  tableId: number;
  tableName: string;
  number: number;
}

/** Người ngồi ghế, dựng từ vé (và tài khoản gắn với vé nếu có). */
export interface AdminSeatTicket {
  id: number;
  displayName: string;
  tiktokUid: string | null;
  username: string | null;
  avatar: string | null;
  guestGroup: string | null;
  status: GalaTicketStatus;
}

export interface AdminSeat {
  id: number;
  number: number;
  ticketId: number | null;
  guestName: string | null;
  assignedAt: string | null;
  ticket: AdminSeatTicket | null;
}

export interface AdminTable {
  id: number;
  eventId: number;
  name: string;
  shape: GalaTableShape;
  capacity: number;
  sortOrder: number;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  seats: AdminSeat[];
}

/** Bàn kèm ghế như POST/PUT/bulk trả về: ghế chưa nối với vé (không có `ticket`) — nạp lại GET .../seating để có. */
export type TableWithSeats = Omit<AdminTable, 'seats'> & {
  seats: Omit<AdminSeat, 'ticket'>[];
};

export interface SeatingCounts {
  tables: number;
  seats: number;
  /** Ghế có vé hoặc tên tay. */
  taken: number;
  /** Vé không bị thu hồi. */
  ticketsActive: number;
  /** Vé còn hiệu lực đang ngồi một ghế. */
  ticketsSeated: number;
  ticketsUnseated: number;
}

/** GET .../seating */
export interface AdminSeatingView {
  visible: boolean;
  counts: SeatingCounts;
  tables: AdminTable[];
}

/** Thân POST/PUT .../seating/tables (PUT: mọi trường tuỳ chọn). */
export interface TableInput {
  name: string;
  shape?: GalaTableShape;
  capacity?: number;
  note?: string | null;
}

/** Thân POST .../seating/tables/bulk: thêm nhanh N bàn tròn 10 ghế tên "{prefix} {k}". */
export interface BulkTablesInput {
  count: number;
  prefix?: string | null;
}

/** Thân PUT .../seating/seats/:sid: gửi `ticketId` HOẶC `guestName`; cả hai null/vắng = bỏ ghế. */
export interface SeatAssignInput {
  ticketId?: number | null;
  guestName?: string | null;
}

/** Kết quả dán danh sách vào bàn (mỗi dòng bị bỏ qua kèm số dòng gốc, 1-based). */
export interface PasteSeatResult {
  assigned: number;
  manual: number;
  skipped: { line: number; reason: string }[];
}

export const TABLE_SHAPE_META: Record<GalaTableShape, { text: string }> = {
  ROUND: { text: 'Bàn tròn' },
  LONG: { text: 'Bàn dài' },
};

/** Số ghế gợi ý khi tạo bàn mới. */
export const TABLE_CAPACITY_DEFAULT: Record<GalaTableShape, number> = { ROUND: 10, LONG: 6 };
export const TABLE_CAPACITY_MAX = 24;
export const BULK_TABLES_MAX = 30;
